import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { fetchWithCorpId, asArray } from '../utils/api';
import { useAuth } from '../utils/auth';
import useDebouncedValue from '../utils/useDebouncedValue';
import Drawer from './Drawer';
import ExpenseForm, { EXPENSE_CATEGORIES } from './ExpenseForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import { FilterTh, SortableTh, StatusBadge } from './ui/TableBits';
import { useToast } from './ui/Toast';
import { useConfirm } from './ui/ConfirmDialog';

function ExpenseTable() {
  const toast = useToast();
  const confirm = useConfirm();
  const { getUserRoles, user } = useAuth();
  const roles = getUserRoles();
  const isAdmin = roles.includes('admin') || roles.includes('super');
  const isStaff = isAdmin || roles.includes('employer');
  const empId = user?.empid;

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  // Per-column filters (issue #144). Status filters server-side (the endpoint
  // already supports it); the rest filter the loaded list client-side, like
  // the old combined search box did.
  const [filter, setFilter] = useState({ employeeName: '', category: '', description: '' });
  const debouncedFilter = useDebouncedValue(filter, 300);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [reloadKey, setReloadKey] = useState(0);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const menuRefs = useRef({});

  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  // A timesheet's expense list links here with ?expenseId=N; open that expense once.
  const location = useLocation();
  const linkedExpenseId = new URLSearchParams(location.search).get('expenseId');
  const openedFromLink = useRef(false);
  useEffect(() => {
    if (linkedExpenseId && !openedFromLink.current) {
      openedFromLink.current = true;
      setDrawer({ mode: 'edit', id: parseInt(linkedExpenseId, 10) });
    }
  }, [linkedExpenseId]);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (exp) => setDrawer({ mode: 'edit', id: exp.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); setReloadKey(k => k + 1); };

  useEffect(() => {
    const close = (e) => {
      if (openActionMenu) {
        const ref = menuRefs.current[openActionMenu];
        if (ref && !ref.contains(e.target)) setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openActionMenu]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    setLoading(true);
    fetchWithCorpId(`/expenses${params.toString() ? `?${params.toString()}` : ''}`)
      .then(data => {
        if (cancelled) return;
        let rows = asArray(data);
        // Employees see only their own expenses.
        if (!isStaff && empId) {
          rows = rows.filter(e => String(e.employeeId) === String(empId));
        }
        setExpenses(rows);
        setError(null);
      })
      .catch(() => { if (!cancelled) { setError('Failed to load expenses'); setExpenses([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter, reloadKey, isStaff, empId]);

  // Client-side per-column filtering and sort over the loaded list.
  const visibleExpenses = useMemo(() => {
    const employeeQ = debouncedFilter.employeeName.trim().toLowerCase();
    const descriptionQ = debouncedFilter.description.trim().toLowerCase();
    const rows = expenses.filter(exp =>
      (!employeeQ || String(exp.employeeName ?? '').toLowerCase().includes(employeeQ)) &&
      (!debouncedFilter.category || exp.category === debouncedFilter.category) &&
      (!descriptionQ || String(exp.description ?? '').toLowerCase().includes(descriptionQ)));
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (key === 'id' || key === 'amount') return (Number(av) - Number(bv)) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
    return rows;
  }, [expenses, debouncedFilter, sortConfig]);

  const totalElements = visibleExpenses.length;
  const totalPages = Math.ceil(totalElements / size);
  const pagedExpenses = visibleExpenses.slice(page * size, (page + 1) * size);

  useEffect(() => { setPage(0); }, [debouncedFilter, statusFilter, size, sortConfig]);
  // Keep the page in range when rows disappear (e.g. after a delete).
  useEffect(() => {
    setPage(p => (totalPages === 0 ? 0 : Math.min(p, totalPages - 1)));
  }, [totalPages]);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));
  const handleFilterChange = (e) => setFilter(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleChangePageSize = (e) => setSize(parseInt(e.target.value, 10));
  const clearFilters = () => { setFilter({ employeeName: '', category: '', description: '' }); setStatusFilter(''); };
  const hasActiveFilters = Boolean(filter.employeeName || filter.category || filter.description || statusFilter);

  const updateStatus = async (id, action) => {
    setOpenActionMenu(null);
    const body = {};
    if (action === 'reject') {
      const reason = await confirm({ title: 'Reject expense', message: 'Tell the employee what to fix.', input: { label: 'Reason', placeholder: 'Optional' }, confirmLabel: 'Reject', danger: true });
      if (reason === null) return;
      if (reason.trim()) body.reviewNotes = reason.trim();
    }
    try {
      await fetchWithCorpId(`/expenses/${id}/${action}`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success(action === 'approve' ? 'Expense approved' : 'Expense rejected');
      setReloadKey(k => k + 1);
    } catch {
      toast.error(`Failed to ${action} expense`);
    }
  };

  const handleDelete = async (id) => {
    setOpenActionMenu(null);
    const ok = await confirm({ title: 'Delete expense', message: 'This deletes the expense and its receipts. This cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await fetchWithCorpId(`/expenses/${id}`, { method: 'DELETE' });
      toast.success('Expense deleted');
      setReloadKey(k => k + 1);
    } catch {
      toast.error('Failed to delete expense');
    }
  };

  return (
    <div className="page-container">
      <PageHeader
        title="Expenses"
        subtitle="Submit expense claims and review them"
        actions={
          <button className="btn-primary" onClick={openCreate}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Expense
          </button>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <tr>
              <SortableTh col="id" label="ID" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="employeeName" label="Employee" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="expenseDate" label="Date" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="category" label="Category" sortConfig={sortConfig} onSort={handleSort} />
              <SortableTh col="amount" label="Amount" sortConfig={sortConfig} onSort={handleSort} />
              <th>Description</th>
              <SortableTh col="status" label="Status" sortConfig={sortConfig} onSort={handleSort} />
              <th>Receipts</th>
              <th>Actions</th>
            </tr>
            <tr>
              <FilterTh />
              <FilterTh label="Employee" name="employeeName" value={filter.employeeName} onChange={handleFilterChange} />
              <FilterTh />
              <FilterTh label="Category" name="category" value={filter.category} onChange={handleFilterChange} options={EXPENSE_CATEGORIES} />
              <FilterTh />
              <FilterTh label="Description" name="description" value={filter.description} onChange={handleFilterChange} />
              <FilterTh label="Status" name="status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} options={['Pending', 'Approved', 'Rejected']} />
              <FilterTh />
              <FilterTh />
            </tr>
          </thead>
          <tbody style={{ opacity: loading && pagedExpenses.length > 0 ? 0.55 : 1 }}>
            {pagedExpenses.length === 0 ? (
              <TableState
                colSpan={9}
                loading={loading}
                error={error}
                onRetry={() => setReloadKey(k => k + 1)}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No expenses yet"
                emptyHint="Create one to submit it for review."
                emptyAction={{ label: 'New Expense', onClick: openCreate }}
              />
            ) : pagedExpenses.map(exp => (
              <tr key={exp.id}>
                <td><span className="table-link" onClick={() => openEdit(exp)}>{exp.id}</span></td>
                <td style={{ fontWeight: '500' }}>{exp.employeeName}</td>
                <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(exp.expenseDate)}</td>
                <td>{exp.category}</td>
                <td style={{ fontWeight: '600', color: 'var(--ink)' }}>{exp.amount}</td>
                <td style={{ color: 'var(--muted)', fontSize: '13px', maxWidth: '220px' }}>{exp.description}</td>
                <td><StatusBadge status={exp.status} /></td>
                <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{exp.receiptCount ?? 0}</td>
                <td style={{ position: 'relative' }} ref={el => menuRefs.current[exp.id] = el}>
                  <button
                    onClick={() => setOpenActionMenu(openActionMenu === exp.id ? null : exp.id)}
                    style={{ background: 'none', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', color: 'var(--muted)', display: 'flex', alignItems: 'center' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                  </button>
                  {openActionMenu === exp.id && (
                    <div className="action-menu">
                      <div className="action-menu-item" onClick={() => { setOpenActionMenu(null); openEdit(exp); }}>View / Edit</div>
                      {isAdmin && exp.status === 'Pending' && (<>
                        <div className="action-menu-item" onClick={() => updateStatus(exp.id, 'approve')}>Approve</div>
                        <div className="action-menu-item" onClick={() => updateStatus(exp.id, 'reject')}>Reject</div>
                      </>)}
                      {(isStaff || exp.status !== 'Approved') && (
                        <div className="action-menu-item" onClick={() => handleDelete(exp.id)}>Delete</div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>{totalElements} total</span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit expense' : 'New expense'}
        subtitle={drawer?.mode === 'edit' ? 'Update the expense and its receipts.' : 'Submit an expense claim for review.'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              {drawer?.mode === 'edit' ? 'Save changes' : 'Create expense'}
            </button>
          </>
        }
      >
        {drawer && (
          <ExpenseForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            expenseId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default ExpenseTable;
